import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    SkeletonMeasuresConstantType,
    SkeletonRadiusConstantType,
    SkeletonSizeConstantType,
    SkeletonLineConstantType,
    SkeletonDynamicStyleType,
    SkeletonRadiusDefault,
    SkeletonLineMeasures,
    SkeletonMeasures,
    SkeletonVariant
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getCornerStyle
} from "../../helpers/theme/corner";

export const SKELETON_VARIANT_RADII: Record<SkeletonVariant, SkeletonRadiusDefault> = {
    block: {
        key: "form",
        divisor: 1
    },
    circle: {
        key: "full",
        divisor: 1
    },
    icon: {
        key: "form",
        divisor: 2
    },
    text: {
        key: "form",
        divisor: 2
    }
};

export const getSkeletonRadius = ({
    radiuses,
    variant,
    radius
}: SkeletonRadiusConstantType): number => {
    if(typeof radius === "number") {
        return radius;
    }

    if(radius) {
        return radiuses[radius];
    }

    const radiusDefault = SKELETON_VARIANT_RADII[variant];

    return radiuses[radiusDefault.key] / radiusDefault.divisor;
};

export const getSkeletonSize = ({
    avatarSizes,
    iconSizes,
    variant,
    size
}: SkeletonSizeConstantType): number => {
    if(typeof size === "number") {
        return size;
    }

    if(size) {
        return avatarSizes[size];
    }

    return variant === "icon" ? iconSizes.medium : avatarSizes.small;
};

export const getSkeletonLine = ({
    typography,
    variant
}: SkeletonLineConstantType): SkeletonLineMeasures => {
    const currentTypography = typography[variant];

    return {
        marginVertical: (currentTypography.lineHeight - currentTypography.fontSize) / 2,
        height: currentTypography.fontSize
    };
};

export const getSkeletonMeasures = ({
    typographyVariant,
    avatarSizes,
    typography,
    iconSizes,
    radiuses,
    variant,
    radius,
    size
}: SkeletonMeasuresConstantType): SkeletonMeasures => {
    const currentLine = getSkeletonLine({
        variant: typographyVariant,
        typography
    });

    return {
        barMarginVertical: currentLine.marginVertical,
        borderRadius: getSkeletonRadius({
            radiuses,
            variant,
            radius
        }),
        barHeight: currentLine.height,
        size: getSkeletonSize({
            avatarSizes,
            iconSizes,
            variant,
            size
        })
    };
};

const stylesheet = StyleSheet.create({
    container: {
        pointerEvents: "none",
        flexShrink: 0,
        width: "100%"
    },
    shape: {
        width: "100%"
    },
    line: {
        width: "100%"
    },
    lastLine: {
        width: "60%"
    }
});

export const useStyles = ({
    currentMeasures,
    controlHeights,
    shapeConfigs,
    aspectRatio,
    variant,
    height,
    width
}: SkeletonDynamicStyleType) => {
    const isSquare = variant === "circle" || variant === "icon";

    const styles = {
        line: {
            marginVertical: currentMeasures.barMarginVertical,
            ...getCornerStyle({
                radius: currentMeasures.borderRadius,
                shapeConfigs
            }),
            height: currentMeasures.barHeight
        } as Mutable<ViewStyle>,
        shape: {
            ...getCornerStyle({
                radius: currentMeasures.borderRadius,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        container: {} as Mutable<ViewStyle>
    };

    if(width !== undefined) {
        styles.container.width = width;

        styles.shape.width = width;
    }

    if(isSquare) {
        styles.shape.height = currentMeasures.size;

        styles.shape.width = currentMeasures.size;

        return styles;
    }

    if(aspectRatio) {
        styles.shape.aspectRatio = aspectRatio;

        return styles;
    }

    styles.shape.height = height ?? controlHeights.medium;

    return styles;
};
export default stylesheet;
